/**
 * Controls.ts — Unified input handling.
 *
 * Desktop mouse modes:
 *   1. "direct"   — Mouse X = roll (right-click held → rudder), Y = pitch.
 *   2. "auto"     — Mouse position = desired heading; the game auto-rolls,
 *                   auto-pitches and auto-rudders toward it.  Mild auto-level
 *                   when the cursor is centred.
 * Also supports:
 *   - Gamepad / Xbox controller (standard Gamepad API mapping)
 *   - Mobile: virtual D-pad or motion (via MobileControls)
 *
 * Desktop settings (sensitivity, invert-Y, mouse mode, dead-zones) are
 * persisted to localStorage and exposed for the start-screen settings panel.
 */
import * as THREE from 'three';
import { MobileControls, isTouchDevice } from './MobileControls';
const DESKTOP_SETTINGS_KEY = 'skyrunner-desktop-settings';
export function defaultDesktopSettings() {
    return { mouseSensitivity: 5, invertY: false, mouseMode: 'direct', gamepadDeadzone: 0.12 };
}
export function loadDesktopSettings() {
    try {
        const raw = localStorage.getItem(DESKTOP_SETTINGS_KEY);
        if (raw)
            return { ...defaultDesktopSettings(), ...JSON.parse(raw) };
    }
    catch { /* ignore */ }
    return defaultDesktopSettings();
}
export function saveDesktopSettings(s) {
    localStorage.setItem(DESKTOP_SETTINGS_KEY, JSON.stringify(s));
}
/* ====================================================================== */
/*  Gamepad helpers                                                       */
/* ====================================================================== */
/** Apply dead-zone and return value in -1…+1. */
function applyDeadzone(value, dz) {
    if (Math.abs(value) < dz)
        return 0;
    const sign = value > 0 ? 1 : -1;
    return sign * (Math.abs(value) - dz) / (1 - dz);
}
/* Standard Gamepad mapping indices (Xbox / "standard" layout)
 *   Axes:  0 = Left Stick X,  1 = Left Stick Y
 *          2 = Right Stick X, 3 = Right Stick Y
 *   Buttons:
 *     0  = A          1  = B          2  = X          3  = Y
 *     4  = LB         5  = RB
 *     6  = LT         7  = RT
 *     8  = Back/View  9  = Start/Menu
 *    10  = L3        11  = R3
 *    12  = D-Up      13  = D-Down     14  = D-Left    15  = D-Right
 */
/* ====================================================================== */
/*  Controls class                                                        */
/* ====================================================================== */
export class Controls {
    constructor(game) {
        /* Normalised axes (-1 … +1) */
        this.pitchInput = 0;
        this.rollInput = 0;
        this.yawInput = 0;
        this.throttleInput = 0; // +1 = increase, -1 = decrease
        this.brake = false;
        this.recover = false; // R key — ONE-SHOT per press, cleared after each update
        /* Auto-level request (set by auto-coordinated mouse mode) */
        this.autoLevel = 0; // 0 = off, >0 = strength (0–1)
        /* Mobile */
        this.mobile = null;
        /** True when a gamepad with "standard" mapping is detected */
        this.gamepadActive = false;
        /* Internal key state */
        this.keys = new Map();
        this.mouseDeltaX = 0;
        this.mouseDeltaY = 0;
        this.rightMouseDown = false;
        this.leftMouseDown = false;
        /**
         * Virtual pitch latch (mouse-only).  Accumulates mouse-Y deltas into a
         * -1…+1 joystick-like position that persists when the mouse stops moving.
         * Only reset to 0 on construction (i.e. each new game session).
         */
        this.mouseVirtualPitch = 0;
        /* Auto-coordinated mode: accumulated virtual cursor position (pixels) */
        this.autoCursorX = 0;
        this.autoCursorY = 0;
        /* Gamepad one-shot helpers (prevent repeated toggles while held) */
        this.gpGearPrev = false;
        this.gpRecoverPrev = false;
        this.gpMenuPrev = false;
        this.connectedGamepadIndex = null;
        this.game = game;
        this.isMobile = isTouchDevice();
        this.desktopSettings = loadDesktopSettings();
        this.onKeyDown = (e) => {
            this.keys.set(e.code, true);
            if (e.code === 'KeyG')
                this.game.player.gearDown = !this.game.player.gearDown;
            if (e.code === 'KeyR')
                this.recover = true;
            if (e.code === 'Escape')
                this.game.togglePause();
        };
        this.onKeyUp = (e) => {
            this.keys.set(e.code, false);
        };
        this.onMouseMove = (e) => {
            if (document.pointerLockElement) {
                this.mouseDeltaX += e.movementX;
                this.mouseDeltaY += e.movementY;
            }
        };
        this.onMouseDown = (e) => {
            if (e.button === 2)
                this.rightMouseDown = true;
            if (e.button === 0)
                this.leftMouseDown = true;
        };
        this.onMouseUp = (e) => {
            if (e.button === 2)
                this.rightMouseDown = false;
            if (e.button === 0)
                this.leftMouseDown = false;
        };
        this.onContextMenu = (e) => { e.preventDefault(); };
        this.onClick = () => {
            if (!this.isMobile && !document.pointerLockElement && !this.gamepadActive) {
                this.game.renderer.domElement.requestPointerLock();
            }
        };
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mousedown', this.onMouseDown);
        window.addEventListener('mouseup', this.onMouseUp);
        this.game.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);
        this.game.renderer.domElement.addEventListener('click', this.onClick);
        /* Gamepad connection events */
        this.onGamepadConnected = (e) => {
            if (e.gamepad.mapping === 'standard') {
                this.connectedGamepadIndex = e.gamepad.index;
                this.gamepadActive = true;
                if (document.pointerLockElement)
                    document.exitPointerLock();
            }
        };
        this.onGamepadDisconnected = (e) => {
            if (e.gamepad.index === this.connectedGamepadIndex) {
                this.connectedGamepadIndex = null;
                this.gamepadActive = false;
            }
        };
        window.addEventListener('gamepadconnected', this.onGamepadConnected);
        window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
        /* Check if a gamepad is already connected at construction */
        this.pollForExistingGamepad();
        /* Initialise mobile controls if on a touch-capable device */
        if (this.isMobile) {
            this.mobile = new MobileControls(game, this);
        }
    }
    /** Check navigator.getGamepads() for an already-connected standard pad. */
    pollForExistingGamepad() {
        try {
            const pads = navigator.getGamepads();
            for (let i = 0; i < pads.length; i++) {
                const gp = pads[i];
                if (gp && gp.mapping === 'standard') {
                    this.connectedGamepadIndex = gp.index;
                    this.gamepadActive = true;
                    return;
                }
            }
        }
        catch { /* Gamepad API not available */ }
    }
    /* ------------------------------------------------------------------ */
    /*  Per-frame update                                                   */
    /* ------------------------------------------------------------------ */
    update(dt) {
        this.autoLevel = 0;
        /* --- Mobile path --- */
        if (this.mobile) {
            this.pitchInput = 0;
            this.rollInput = 0;
            this.yawInput = 0;
            this.throttleInput = 0;
            this.mobile.update(dt);
            return;
        }
        /* --- Gamepad path --- */
        if (this.gamepadActive) {
            this.updateGamepad(dt);
            return;
        }
        /* --- Mouse + Keyboard path --- */
        if (this.desktopSettings.mouseMode === 'auto') {
            this.updateMouseAuto(dt);
        }
        else {
            this.updateMouseDirect(dt);
        }
    }
    /* ------------------------------------------------------------------ */
    /*  Mouse Direct mode                                                  */
    /*  X → roll (right-click held → rudder instead),  Y → pitch          */
    /* ------------------------------------------------------------------ */
    updateMouseDirect(dt) {
        const k = (code) => this.keys.get(code) ?? false;
        const invertY = this.desktopSettings.invertY ? -1 : 1;
        /* Keyboard pitch/roll/yaw (held keys behave like a held stick — no latch needed) */
        const kbPitch = ((k('KeyS') ? 1 : 0) - (k('KeyW') ? 1 : 0)) * invertY;
        this.rollInput = (k('KeyA') ? 1 : 0) - (k('KeyD') ? 1 : 0);
        this.yawInput = (k('KeyQ') ? 1 : 0) - (k('KeyE') ? 1 : 0);
        this.brake = k('Space');
        /* Mouse buttons control throttle (same as auto mode) */
        const kbThrottle = (k('ShiftLeft') || k('ShiftRight') ? 1 : 0)
            - (k('ControlLeft') || k('ControlRight') ? 1 : 0);
        const mouseThrottle = this.leftMouseDown ? 1 : (this.rightMouseDown ? -1 : 0);
        this.throttleInput = THREE.MathUtils.clamp(kbThrottle + mouseThrottle, -1, 1);
        const sens = 0.003 * this.desktopSettings.mouseSensitivity;
        /* Mouse X always drives roll */
        this.rollInput -= this.mouseDeltaX * sens;
        /* Virtual pitch latch: accumulate mouse-Y into a held position (-1…+1).
           A gentle decay nudges it slowly back toward neutral when the mouse is still,
           giving a hint of trim feel without fighting the player. */
        this.mouseVirtualPitch = THREE.MathUtils.clamp(this.mouseVirtualPitch + this.mouseDeltaY * sens * invertY, -1, 1);
        this.mouseVirtualPitch = THREE.MathUtils.lerp(this.mouseVirtualPitch, 0, Controls.MOUSE_PITCH_DECAY * dt);
        this.pitchInput = THREE.MathUtils.clamp(this.mouseVirtualPitch + kbPitch, -1, 1);
        this.mouseDeltaX = 0;
        this.mouseDeltaY = 0;
    }
    /* ------------------------------------------------------------------ */
    /*  Mouse Auto-Coordinated mode                                       */
    /*  Mouse position = desired direction; game auto-rolls/pitches/yaws  */
    /* ------------------------------------------------------------------ */
    updateMouseAuto(dt) {
        const k = (code) => this.keys.get(code) ?? false;
        const invertY = this.desktopSettings.invertY ? -1 : 1;
        /* Keyboard provides throttle/brake; in auto-mode mouse buttons also control throttle */
        const kbThrottle = (k('ShiftLeft') || k('ShiftRight') ? 1 : 0)
            - (k('ControlLeft') || k('ControlRight') ? 1 : 0);
        const mouseThrottle = this.leftMouseDown ? 1 : (this.rightMouseDown ? -1 : 0);
        this.throttleInput = THREE.MathUtils.clamp(kbThrottle + mouseThrottle, -1, 1);
        this.brake = k('Space');
        /* Allow keyboard to add roll/pitch/yaw overrides on top */
        let kbPitch = ((k('KeyS') ? 1 : 0) - (k('KeyW') ? 1 : 0)) * invertY;
        let kbRoll = (k('KeyA') ? 1 : 0) - (k('KeyD') ? 1 : 0);
        let kbYaw = (k('KeyQ') ? 1 : 0) - (k('KeyE') ? 1 : 0);
        /* Accumulate mouse-X into the virtual roll cursor (decays back to centre for auto-level) */
        const sens = 0.8 * this.desktopSettings.mouseSensitivity;
        this.autoCursorX += this.mouseDeltaX * sens * 0.1;
        /* Virtual pitch latch: mouse-Y accumulates into a held -1…+1 position.
           A gentle decay drifts it slowly back toward neutral when the mouse is still. */
        this.mouseVirtualPitch = THREE.MathUtils.clamp(this.mouseVirtualPitch + this.mouseDeltaY * 0.003 * this.desktopSettings.mouseSensitivity * invertY, -1, 1);
        this.mouseVirtualPitch = THREE.MathUtils.lerp(this.mouseVirtualPitch, 0, Controls.MOUSE_PITCH_DECAY * dt);
        this.mouseDeltaX = 0;
        this.mouseDeltaY = 0;
        /* Decay autoCursorX toward centre (auto-return) */
        const decay = 1 - Math.min(1, Controls.AUTO_CURSOR_DECAY * dt / Controls.AUTO_CURSOR_MAX);
        this.autoCursorX *= decay;
        /* Clamp and normalise autoCursorX to -1…+1 */
        const maxC = Controls.AUTO_CURSOR_MAX;
        this.autoCursorX = THREE.MathUtils.clamp(this.autoCursorX, -maxC, maxC);
        const nx = this.autoCursorX / maxC; // positive = right
        /* Auto-level triggers when roll cursor is near centre */
        const absCursor = Math.abs(nx);
        /* Roll toward the target direction (mouse-X drives bank angle) */
        this.rollInput = THREE.MathUtils.clamp(-nx * 1.5 + kbRoll, -1, 1);
        /* Pitch from the latched virtual pitch position */
        this.pitchInput = THREE.MathUtils.clamp(this.mouseVirtualPitch + kbPitch, -1, 1);
        /* Auto-rudder: coordinate the turn — yaw proportional to roll */
        this.yawInput = THREE.MathUtils.clamp(-this.rollInput * 0.45 + kbYaw, -1, 1);
        /* If the roll cursor is near centre, request mild auto-level (roll only) */
        if (absCursor < 0.08) {
            this.autoLevel = 1.0 - absCursor / 0.08;
        }
    }
    /* ------------------------------------------------------------------ */
    /*  Gamepad (Xbox / standard layout)                                   */
    /* ------------------------------------------------------------------ */
    updateGamepad(_dt) {
        const gp = this.getGamepad();
        if (!gp) {
            this.gamepadActive = false;
            return;
        }
        const dz = this.desktopSettings.gamepadDeadzone;
        const invertY = this.desktopSettings.invertY ? -1 : 1;
        this.rollInput = -applyDeadzone(gp.axes[0] ?? 0, dz);
        this.pitchInput = applyDeadzone(gp.axes[1] ?? 0, dz) * invertY;
        this.yawInput = -applyDeadzone(gp.axes[2] ?? 0, dz);
        const rt = gp.buttons[7]?.value ?? 0;
        const lt = gp.buttons[6]?.value ?? 0;
        this.throttleInput = rt - lt;
        this.brake = lt > 0.5;
        const btnA = gp.buttons[0]?.pressed ?? false;
        const btnB = gp.buttons[1]?.pressed ?? false;
        const btnStart = gp.buttons[9]?.pressed ?? false;
        /* Skip flight-control buttons while a menu overlay is showing so that
           the MenuGamepadPoller can handle them instead (e.g. mission-complete). */
        const missionCompleteEl = document.getElementById('mission-complete');
        const overlayOpen = missionCompleteEl != null && !missionCompleteEl.classList.contains('hidden');
        if (!overlayOpen) {
            if (btnA && !this.gpGearPrev)
                this.game.player.gearDown = !this.game.player.gearDown;
            if (btnB && !this.gpRecoverPrev)
                this.recover = true;
        }
        if (btnStart && !this.gpMenuPrev)
            this.game.togglePause();
        this.gpGearPrev = btnA;
        this.gpRecoverPrev = btnB;
        this.gpMenuPrev = btnStart;
        const dUp = gp.buttons[12]?.pressed ?? false;
        const dDown = gp.buttons[13]?.pressed ?? false;
        const dLeft = gp.buttons[14]?.pressed ?? false;
        const dRight = gp.buttons[15]?.pressed ?? false;
        if (dUp)
            this.pitchInput = Math.min(this.pitchInput + (-1 * invertY), 1);
        if (dDown)
            this.pitchInput = Math.max(this.pitchInput + (1 * invertY), -1);
        if (dLeft)
            this.rollInput = Math.min(this.rollInput + 1, 1);
        if (dRight)
            this.rollInput = Math.max(this.rollInput - 1, -1);
        if (gp.buttons[4]?.pressed)
            this.yawInput = Math.min(this.yawInput + 1, 1);
        if (gp.buttons[5]?.pressed)
            this.yawInput = Math.max(this.yawInput - 1, -1);
        if (this.mouseDeltaX !== 0 || this.mouseDeltaY !== 0) {
            this.mouseDeltaX = 0;
            this.mouseDeltaY = 0;
        }
    }
    getGamepad() {
        if (this.connectedGamepadIndex === null)
            return null;
        try {
            return navigator.getGamepads()[this.connectedGamepadIndex] ?? null;
        }
        catch {
            return null;
        }
    }
    /* ------------------------------------------------------------------ */
    /*  Cleanup                                                            */
    /* ------------------------------------------------------------------ */
    dispose() {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mousedown', this.onMouseDown);
        window.removeEventListener('mouseup', this.onMouseUp);
        window.removeEventListener('gamepadconnected', this.onGamepadConnected);
        window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
        this.game.renderer.domElement.removeEventListener('contextmenu', this.onContextMenu);
        this.game.renderer.domElement.removeEventListener('click', this.onClick);
        if (document.pointerLockElement)
            document.exitPointerLock();
        this.mobile?.dispose();
        this.mobile = null;
    }
}
/** Cursor return-to-centre speed (pixels/sec) */
Controls.AUTO_CURSOR_DECAY = 120;
/** Max virtual cursor offset before clamping (pixels) */
Controls.AUTO_CURSOR_MAX = 300;
/**
 * How fast the virtual pitch latch drifts back to neutral when the mouse is still.
 * Uses exponential decay: value(t) ≈ value(0) · e^(−RATE · t)
 * 1.0 → ~3 s to near-zero  |  0.5 → ~6 s  |  0.12 → ~25 s (barely felt)
 */
Controls.MOUSE_PITCH_DECAY = 1.0;
/* ====================================================================== */
/*  MenuGamepadPoller — independent RAF loop for controller menu nav      */
/*                                                                        */
/*  Standard console-style focus cursor:                                  */
/*    D-pad Up/Down or Left Stick Y  → move focus between items           */
/*    D-pad Left/Right or LB/RB      → cycle <select> options             */
/*    A / Start                      → confirm / cycle forward            */
/*    B                              → cycle <select> backward            */
/*                                                                        */
/*  The currently focused element receives the CSS class "gp-focused"     */
/*  which style.css renders as a green glow ring.                         */
/* ====================================================================== */
export class MenuGamepadPoller {
    constructor() {
        this.active = false;
        this.rafId = null;
        /** Cursor position within the current menu's item list. */
        this.focusedIndex = 1;
        /** When > performance.now(), all NEW button presses are ignored. */
        this.cooldownUntil = 0;
        /* One-shot previous-frame button states */
        this.prevStart = false;
        this.prevA = false;
        this.prevB = false;
        this.prevDUp = false;
        this.prevDDown = false;
        this.prevDLeft = false;
        this.prevDRight = false;
        this.prevLB = false;
        this.prevRB = false;
        /**
         * Stick auto-repeat: timestamp of when the next stick navigation fires.
         * Reset to 0 when the stick returns to neutral.
         */
        this.stickNextFire = 0;
        this.stickInitialFired = false;
        /* ------------------------------------------------------------------ */
        /*  RAF loop                                                            */
        /* ------------------------------------------------------------------ */
        this.loop = () => {
            if (!this.active)
                return;
            this.poll();
            this.rafId = requestAnimationFrame(this.loop);
        };
    }
    /* ------------------------------------------------------------------ */
    /*  Lifecycle                                                           */
    /* ------------------------------------------------------------------ */
    /** Call once at app start — runs forever in the background. */
    start() {
        if (this.active)
            return;
        this.active = true;
        this.loop();
    }
    /** Shut down completely (only needed if the whole app is torn down). */
    stop() {
        this.active = false;
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.clearFocus();
    }
    /**
     * Snapshot current button states and start a short dead-zone.
     * Call this the moment the game enters a menu/pause state so the button
     * that caused the transition doesn't immediately fire again.
     */
    syncState() {
        this.cooldownUntil = performance.now() + 400;
        /* Reset focus to mission select (index 1) whenever we enter a menu */
        this.focusedIndex = 1;
        try {
            const pads = navigator.getGamepads();
            for (let i = 0; i < pads.length; i++) {
                const gp = pads[i];
                if (gp?.mapping === 'standard') {
                    this.prevStart = gp.buttons[9]?.pressed ?? false;
                    this.prevA = gp.buttons[0]?.pressed ?? false;
                    this.prevB = gp.buttons[1]?.pressed ?? false;
                    this.prevDUp = gp.buttons[12]?.pressed ?? false;
                    this.prevDDown = gp.buttons[13]?.pressed ?? false;
                    this.prevDLeft = gp.buttons[14]?.pressed ?? false;
                    this.prevDRight = gp.buttons[15]?.pressed ?? false;
                    this.prevLB = gp.buttons[4]?.pressed ?? false;
                    this.prevRB = gp.buttons[5]?.pressed ?? false;
                    return;
                }
            }
        }
        catch { /* Gamepad API unavailable */ }
    }
    /* ------------------------------------------------------------------ */
    /*  Menu item discovery                                                 */
    /* ------------------------------------------------------------------ */
    /**
     * Returns the ordered list of interactive elements for whichever
     * menu / overlay is currently visible.
     * Order = top-to-bottom as rendered: selects before their confirm button.
     */
    getMenuItems() {
        /* Mission-complete overlay takes priority */
        const missionComplete = document.getElementById('mission-complete');
        if (missionComplete && !missionComplete.classList.contains('hidden')) {
            return [
                document.getElementById('next-mission-select'),
                document.getElementById('next-mission-btn'),
            ].filter((el) => el !== null);
        }
        /* Game-over overlay */
        const gameOver = document.getElementById('game-over');
        if (gameOver && !gameOver.classList.contains('hidden')) {
            return [
                document.getElementById('restart-btn'),
            ].filter((el) => el !== null);
        }
        /* Start / pause screen */
        const startScreen = document.getElementById('start-screen');
        if (startScreen && !startScreen.classList.contains('hidden')) {
            const items = [
                document.getElementById('setting-invert-y-desktop'),
                document.getElementById('start-mission-select'),
                document.getElementById('start-btn'),
            ];
            const restartPauseBtn = document.getElementById('restart-pause-btn');
            if (restartPauseBtn && !restartPauseBtn.classList.contains('hidden')) {
                items.push(restartPauseBtn);
            }
            return items.filter((el) => el !== null);
        }
        return [];
    }
    /* ------------------------------------------------------------------ */
    /*  Focus helpers                                                       */
    /* ------------------------------------------------------------------ */
    clearFocus() {
        document.querySelectorAll('.gp-focused').forEach(el => el.classList.remove('gp-focused'));
    }
    setFocus(items, index) {
        this.clearFocus();
        if (items.length === 0)
            return;
        this.focusedIndex = Math.max(0, Math.min(items.length - 1, index));
        items[this.focusedIndex].classList.add('gp-focused');
    }
    poll() {
        let gp = null;
        try {
            const pads = navigator.getGamepads();
            for (let i = 0; i < pads.length; i++) {
                if (pads[i]?.mapping === 'standard') {
                    gp = pads[i];
                    break;
                }
            }
        }
        catch {
            return;
        }
        const items = this.getMenuItems();
        /* No gamepad or no menu visible — clear visual focus and bail */
        if (!gp || items.length === 0) {
            this.clearFocus();
            if (!gp) {
                /* Reset all prev states so there's no phantom edge on reconnect */
                this.prevStart = this.prevA = this.prevB = false;
                this.prevDUp = this.prevDDown = this.prevDLeft = this.prevDRight = false;
                this.prevLB = this.prevRB = false;
            }
            return;
        }
        /* Clamp focus index in case the item list changed (e.g. restart-btn appeared) */
        if (this.focusedIndex >= items.length)
            this.focusedIndex = items.length - 1;
        /* Read buttons */
        const btnStart = gp.buttons[9]?.pressed ?? false;
        const btnA = gp.buttons[0]?.pressed ?? false;
        const btnB = gp.buttons[1]?.pressed ?? false;
        const dUp = gp.buttons[12]?.pressed ?? false;
        const dDown = gp.buttons[13]?.pressed ?? false;
        const dLeft = gp.buttons[14]?.pressed ?? false;
        const dRight = gp.buttons[15]?.pressed ?? false;
        const lb = gp.buttons[4]?.pressed ?? false;
        const rb = gp.buttons[5]?.pressed ?? false;
        const stickY = gp.axes[1] ?? 0; // left stick Y (+1 = down)
        const now = performance.now();
        const inCooldown = now < this.cooldownUntil;
        if (!inCooldown) {
            /* ---- Up / Down navigation ------------------------------------ */
            const dpadUp = dUp && !this.prevDUp;
            const dpadDown = dDown && !this.prevDDown;
            /* Left-stick auto-repeat navigation */
            let stickUp = false;
            let stickDown = false;
            if (stickY < -0.5) {
                if (now >= this.stickNextFire) {
                    stickUp = true;
                    this.stickNextFire = now + (this.stickInitialFired
                        ? MenuGamepadPoller.STICK_REPEAT_DELAY
                        : MenuGamepadPoller.STICK_INITIAL_DELAY);
                    this.stickInitialFired = true;
                }
            }
            else if (stickY > 0.5) {
                if (now >= this.stickNextFire) {
                    stickDown = true;
                    this.stickNextFire = now + (this.stickInitialFired
                        ? MenuGamepadPoller.STICK_REPEAT_DELAY
                        : MenuGamepadPoller.STICK_INITIAL_DELAY);
                    this.stickInitialFired = true;
                }
            }
            else {
                /* Stick returned to neutral — reset auto-repeat */
                this.stickNextFire = 0;
                this.stickInitialFired = false;
            }
            if (dpadUp || stickUp) {
                this.setFocus(items, this.focusedIndex - 1);
            }
            else if (dpadDown || stickDown) {
                this.setFocus(items, this.focusedIndex + 1);
            }
            else {
                /* No navigation this frame — just keep current highlight fresh */
                this.setFocus(items, this.focusedIndex);
            }
            /* ---- Left / Right → cycle the focused <select> --------------- */
            const cycleLeft = (dLeft && !this.prevDLeft) || (lb && !this.prevLB);
            const cycleRight = (dRight && !this.prevDRight) || (rb && !this.prevRB);
            if (cycleLeft || cycleRight) {
                const focused = items[this.focusedIndex];
                if (focused?.tagName === 'SELECT') {
                    this.shiftSelect(focused, cycleLeft ? -1 : 1);
                }
            }
            /* ---- A / Start → confirm / cycle forward -------------------- */
            const confirmPressed = (btnA && !this.prevA) || (btnStart && !this.prevStart);
            if (confirmPressed) {
                const focused = items[this.focusedIndex];
                if (focused) {
                    if (focused.tagName === 'SELECT') {
                        this.shiftSelect(focused, 1);
                    }
                    else if (focused.tagName === 'INPUT' &&
                        focused.type === 'checkbox') {
                        focused.checked = !focused.checked;
                        focused.dispatchEvent(new Event('change'));
                    }
                    else {
                        focused.click();
                    }
                }
            }
            /* ---- B → cycle focused <select> backward --------------------- */
            const bPressed = btnB && !this.prevB;
            if (bPressed) {
                const focused = items[this.focusedIndex];
                if (focused?.tagName === 'SELECT') {
                    this.shiftSelect(focused, -1);
                }
            }
        }
        else {
            /* In cooldown — still paint the focus ring so there's no visible gap */
            this.setFocus(items, this.focusedIndex);
        }
        /* Always update previous-frame states */
        this.prevStart = btnStart;
        this.prevA = btnA;
        this.prevB = btnB;
        this.prevDUp = dUp;
        this.prevDDown = dDown;
        this.prevDLeft = dLeft;
        this.prevDRight = dRight;
        this.prevLB = lb;
        this.prevRB = rb;
    }
    shiftSelect(sel, delta) {
        const next = Math.max(0, Math.min(sel.options.length - 1, sel.selectedIndex + delta));
        if (next !== sel.selectedIndex) {
            sel.selectedIndex = next;
            sel.dispatchEvent(new Event('change'));
        }
    }
}
MenuGamepadPoller.STICK_INITIAL_DELAY = 400; // ms before first repeat
MenuGamepadPoller.STICK_REPEAT_DELAY = 150; // ms between repeats
